"use client";

export const CONSENT_STORAGE_KEY = "cookie_consent_v2";
export const CONSENT_UPDATED_EVENT = "cookie-consent-updated";
export const CONSENT_OPEN_EVENT = "cookie-consent-open";

export type CookieConsent = {
  essential: true;
  analytics: boolean;
  personalization: boolean;
  marketing: boolean;
  updatedAt: string;
};

export type ConsentStatus = "unknown" | "saved";

export type ConsentLoadResult = {
  consent: CookieConsent;
  exists: boolean;
};

export const DEFAULT_CONSENT: CookieConsent = {
  essential: true,
  analytics: false,
  personalization: false,
  marketing: false,
  updatedAt: new Date(0).toISOString()
};

export function defaultConsent(): CookieConsent {
  return {
    ...DEFAULT_CONSENT,
    updatedAt: new Date().toISOString()
  };
}

export function acceptAllConsent(): CookieConsent {
  const now = new Date().toISOString();
  return {
    essential: true,
    analytics: true,
    personalization: true,
    marketing: true,
    updatedAt: now
  };
}

export function essentialOnlyConsent(): CookieConsent {
  const now = new Date().toISOString();
  return {
    essential: true,
    analytics: false,
    personalization: false,
    marketing: false,
    updatedAt: now
  };
}

function sanitizeBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === "boolean" ? value : fallback;
}

export function sanitizeConsent(raw: Partial<CookieConsent> | null | undefined): CookieConsent {
  if (!raw) {
    return defaultConsent();
  }

  const updatedAt =
    typeof raw.updatedAt === "string" ? raw.updatedAt : new Date().toISOString();

  return {
    essential: true,
    analytics: sanitizeBoolean(raw.analytics, false),
    personalization: sanitizeBoolean(raw.personalization, false),
    marketing: sanitizeBoolean(raw.marketing, false),
    updatedAt
  };
}

export function loadConsent(): ConsentLoadResult {
  if (typeof window === "undefined") {
    return { consent: defaultConsent(), exists: false };
  }

  const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);

  if (!raw) {
    return { consent: defaultConsent(), exists: false };
  }

  try {
    const parsed = JSON.parse(raw) as Partial<CookieConsent>;
    return { consent: sanitizeConsent(parsed), exists: true };
  } catch {
    return { consent: defaultConsent(), exists: false };
  }
}

export function saveConsent(preferences: Partial<CookieConsent>): CookieConsent {
  const consent = sanitizeConsent({
    ...preferences,
    updatedAt: new Date().toISOString()
  });

  if (typeof window !== "undefined") {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(consent));
    window.dispatchEvent(new Event(CONSENT_UPDATED_EVENT));
  }

  return consent;
}

export function hasAnalyticsConsent(consent?: CookieConsent): boolean {
  const effective = consent ?? loadConsent().consent;
  return Boolean(effective.analytics);
}
